/**
 * /status — System status page.
 *
 * 2026-05-17 polish bundle (F4c): the prior implementation hardcoded 10
 * component rows to `status: 'operational'` while the Better Stack page
 * (BETTER_STACK_STATUS_URL) was a placeholder. That created false
 * "all systems operational" green-light copy on a page Paul hadn't actually
 * provisioned. Re-audit flagged this as misleading. The page now:
 *
 *   1. Shows an honest "Monitoring is being provisioned" banner while the
 *      Better Stack page is unprovisioned (default state today).
 *   2. Renders the Better Stack iframe ONLY when
 *      `import.meta.env.VITE_STATUS_PAGE_READY === 'true'` AND a non-null
 *      `BETTER_STACK_STATUS_URL` is configured. The env-flag is wired in
 *      the runbook at
 *        docs-vault/07-runbooks/observability/better-stack-status-page.md
 *      Once Paul completes that runbook, set the flag to flip the page to
 *      the live widget.
 *   3. Lets the manual component grid be re-introduced ONLY behind the
 *      same `STATUS_PAGE_READY` flag — fabricated "operational" rows are
 *      not shown when the upstream is unprovisioned.
 *
 * The `?nowidget=1` query param disables the iframe — used by smoke tests
 * + screenshot diffing so the deterministic banner view is what gets
 * snapshotted.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { MarketingLayout } from '../components/layouts/marketing-layout';

export const Route = createFileRoute('/status')({
  component: StatusPage,
});

/**
 * Public Better Stack status page URL. Replace with the real
 * `https://status.elreign.io` (or the
 * `https://elreign.betteruptime.com/badge/<id>` widget URL) once the
 * runbook step "create public status page" is complete.
 *
 * Leaving as null hides the widget shell.
 */
export const BETTER_STACK_STATUS_URL: string | null =
  'https://status.elreign.io';

/**
 * Gate for both the upstream Better Stack iframe AND the component grid.
 * Reads `VITE_STATUS_PAGE_READY` from the Vite build env — falsy by
 * default. Set to `'true'` only after the runbook is complete and the
 * status page actually exists at the URL above.
 *
 * Why an env var rather than a feature flag in code: this is a deploy-
 * time provisioning gate, not a per-user toggle. The component readiness
 * is bound to the deployment environment.
 */
function statusPageReady(): boolean {
  // `import.meta.env.VITE_STATUS_PAGE_READY` is undefined in the test
  // runner (jsdom) unless explicitly set, which is the correct default —
  // tests should see the "provisioning" banner, not a stub iframe.
  const raw =
    typeof import.meta !== 'undefined' && import.meta.env
      ? import.meta.env.VITE_STATUS_PAGE_READY
      : undefined;
  return raw === 'true' || raw === true;
}

function StatusPage() {
  useEffect(() => {
    document.title = 'Status · Elreign';
    const meta = document.querySelector('meta[name="description"]') ?? (() => {
      const m = document.createElement('meta');
      m.name = 'description';
      document.head.appendChild(m);
      return m;
    })();
    meta.setAttribute(
      'content',
      'Current status of Elreign services. Tenant application, signing, audit chain, payments, and notification rails, all surfaced here.',
    );
  }, []);

  const ready = statusPageReady();

  // The widget can be suppressed with ?nowidget=1 — used by deterministic
  // smoke tests and screenshot diffing so the banner below is what gets
  // snapshotted (the iframe itself is a third-party render).
  const showWidget =
    ready &&
    BETTER_STACK_STATUS_URL !== null &&
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('nowidget') !== '1';

  return (
    <MarketingLayout>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '72px 24px 96px' }}>
        {/* Hero */}
        <header style={{ marginBottom: 40 }}>
          <p className="eyebrow" style={{ marginBottom: 18 }}>System status</p>
          <h1
            className="serif"
            style={{
              fontSize: 'clamp(36px, 5.5vw, 52px)',
              fontStyle: 'italic',
              fontWeight: 300,
              color: 'var(--ink)',
              lineHeight: 1.1,
              letterSpacing: '-0.025em',
              margin: '0 0 24px',
            }}
          >
            {ready ? (
              <>System <em>status.</em></>
            ) : (
              <>Monitoring is <em>being provisioned.</em></>
            )}
          </h1>
          {!ready && (
            <p style={{ fontSize: 16, color: 'var(--ink-2)', margin: 0, lineHeight: 1.55 }}>
              We&rsquo;re standing up a synthetic-monitoring page right now. Until it&rsquo;s
              live, please subscribe to incident notifications below: we email designated
              firm contacts within fifteen minutes of any confirmed SEV-1 or SEV-2 incident.
            </p>
          )}
        </header>

        {/* Better Stack widget — shows live synthetic-monitor uptime when wired.
            Hidden by ?nowidget=1 + when VITE_STATUS_PAGE_READY is not 'true'. */}
        {showWidget && BETTER_STACK_STATUS_URL && (
          <BetterStackWidget url={BETTER_STACK_STATUS_URL} />
        )}

        {/* Subscribe — always shown so the page is useful even when unprovisioned. */}
        <section
          style={{
            padding: '28px 32px',
            background: 'var(--primary-700)',
            color: '#FAF7F0',
            borderRadius: 'var(--r-xl)',
            marginBottom: 48,
          }}
        >
          <p className="eyebrow on-dark" style={{ marginBottom: 10 }}>
            Stay informed
          </p>
          <h3
            className="serif"
            style={{
              fontSize: 22,
              fontStyle: 'italic',
              fontWeight: 300,
              color: '#FAF7F0',
              margin: '0 0 10px',
              letterSpacing: '-0.01em',
            }}
          >
            Subscribe to incident notifications
          </h3>
          <p
            style={{
              fontSize: 15,
              color: 'rgba(250,247,240,0.85)',
              lineHeight: 1.6,
              margin: '0 0 16px',
              maxWidth: 500,
            }}
          >
            We email designated firm contacts within fifteen minutes of any confirmed SEV-1 or
            SEV-2 incident. Add a contact at any time by writing to operations@elreign.io.
          </p>
          <a
            href="mailto:operations@elreign.io?subject=Status%20notifications"
            style={{
              background: 'var(--accent-400)',
              color: 'var(--primary-900)',
              padding: '11px 22px',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              textDecoration: 'none',
              display: 'inline-block',
            }}
          >
            Subscribe
          </a>
        </section>

        {/* Incident history — always shown. */}
        <section>
          <h2
            className="serif"
            style={{
              fontSize: 22,
              fontStyle: 'italic',
              fontWeight: 300,
              color: 'var(--ink)',
              margin: '0 0 16px',
              letterSpacing: '-0.01em',
            }}
          >
            Recent incidents
          </h2>
          <div
            style={{
              padding: '20px 22px',
              background: 'var(--surface)',
              border: '1px solid var(--hairline)',
              borderRadius: 'var(--r-md)',
            }}
          >
            <p style={{ fontSize: 15, color: 'var(--ink-2)', margin: 0, lineHeight: 1.6 }}>
              Incident history is retained on the audit chain and is available on request from{' '}
              <a href="mailto:operations@elreign.io" style={{ color: 'var(--primary-600)' }}>
                operations@elreign.io
              </a>
              .
            </p>
          </div>
        </section>
      </div>
    </MarketingLayout>
  );
}

/**
 * Better Stack live uptime widget. Embedded as an iframe sandboxed with the
 * minimum permissions needed for the upstream widget to render (scripts +
 * same-origin for cross-frame messages back to status.elreign.io). The
 * `loading="lazy"` attribute defers the network round-trip until the user
 * scrolls into the section — keeps Time to Interactive on /status itself
 * sub-second even on a slow link.
 *
 * The fallback link is shown beneath so screen-reader users who can't
 * interact with the iframe still have a path to the same data, and so the
 * page survives gracefully if Better Stack is down (the iframe will look
 * empty, the link still goes to a cached page on the status.elreign.io
 * subdomain).
 */
function BetterStackWidget({ url }: { url: string }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <section
      aria-label="Live uptime"
      data-testid="status-widget"
      style={{
        border: '1px solid var(--hairline)',
        borderRadius: 'var(--r-md)',
        overflow: 'hidden',
        marginBottom: 32,
        background: 'var(--surface)',
      }}
    >
      <iframe
        src={url}
        title="Elreign live uptime: synthetic monitoring for api.elreign.io"
        loading="lazy"
        onLoad={() => setLoaded(true)}
        sandbox="allow-scripts allow-same-origin"
        referrerPolicy="no-referrer"
        style={{
          width: '100%',
          height: loaded ? 260 : 0,
          border: 'none',
          display: 'block',
          background: 'var(--bg)',
        }}
      />
      <div
        style={{
          padding: '12px 16px',
          borderTop: loaded ? '1px solid var(--hairline)' : 'none',
          fontSize: 12,
          color: 'var(--ink-3)',
          textAlign: 'right',
        }}
      >
        {loaded ? (
          <>
            Live data via Better Stack ·{' '}
            <a
              href={url}
              target="_blank"
              rel="noreferrer noopener"
              style={{ color: 'var(--primary-600)' }}
            >
              open full status page
            </a>
          </>
        ) : (
          <>
            Loading live uptime ·{' '}
            <a
              href={url}
              target="_blank"
              rel="noreferrer noopener"
              style={{ color: 'var(--primary-600)' }}
            >
              open in new tab
            </a>
          </>
        )}
      </div>
    </section>
  );
}
